import React from "react";
import { makeStyles } from "@material-ui/core";
import ResponsiveWrapper from "components/charts/ResponsiveWrapper";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
    container: {
        margin: theme.spacing(2),
        borderRadius: `${theme.spacing(1)}px ${theme.spacing(1)}px 0px 0px`,
        color: theme.palette.text.primary,
        overflowX: "auto",
        maxHeight: ({ height }) => height,
        height: ({ height }) => height,
        backgroundColor: theme.palette.table.primaryBase,
        '&::-webkit-scrollbar': {
            height: 6,
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.action.disabled,
        },
    },
    table: {
        borderCollapse: "collapse",
        width: "100%",
        // minWidth: ({ width }) => width - theme.spacing(10), // Account for container padding
    },
    th: {
        padding: theme.spacing(1.25),
    },
    td: {
        padding: theme.spacing(1.25),
        textAlign: "center",
        borderBottom: `1px solid ${theme.palette.divider}`
    },
    groupHeader: {
        textAlign: "center",
        padding: theme.spacing(1.25),
    },
    subHeader: {
        textAlign: "center",
        opacity: 0.85,
        padding: theme.spacing(1.25),
    },
    thead: {
        backgroundColor: theme.palette.secondary.base,
        position: "sticky",
        top: 0,
        left: 0,
        textAlign: "left",
        zIndex: 2
    }
}));

const NoDataText = ({ loading, svgBoundaries, centerPos, themeType }) => {
    const { t } = useTranslation();
    const text = loading ? "loading" : "no_data_found";

    return (
        <svg width={svgBoundaries.width} height={svgBoundaries.height}>
            <g transform={`translate(${centerPos})`} data-testid={text} >
                <text textAnchor="middle" data-testid={`${text}-text`}
                    fill={themeType === "dark" ? "white" : "black"}>
                    {t(text)}
                </text>
            </g>
        </svg>
    )
}

const GroupedMatrixTable = ({
    data = [],
    rowKey = "name",
    getNestedData,
    loading,
    themeType = "dark",
    height,
    ...rest
}) => {
    const classes = useStyles({ height });
    
    const svgBoundaries = {
        width: rest.parentWidth,
        height: height
    };

    const centerPos = [svgBoundaries.width / 2, svgBoundaries.height / 2];
    const firstNested = data.length > 0 ? getNestedData(data[0]) : {};
    const columnGroups = Object.keys(firstNested || {});
    const subColumns = Array.from(
        new Set(
            data.flatMap(row =>
                columnGroups.flatMap(group =>
                    Object.keys(getNestedData(row)?.[group] || {})
                )
            )
        )
    );

    return (
        <div data-testid="grouped-matrix-table">
            {!data?.length
                ? <NoDataText
                    loading={loading}
                    svgBoundaries={svgBoundaries}
                    centerPos={centerPos}
                    themeType={themeType} 
                />
                : <div className={classes.container}>
                    <table className={classes.table}>
                        <thead className={classes.thead}>
                            <tr>
                                <th>
                                </th>
                                {columnGroups.map(group => (
                                    <th
                                        key={group}
                                        colSpan={subColumns.length}
                                        className={classes.groupHeader}
                                    >
                                        {group}
                                    </th>
                                ))}
                            </tr>
                            <tr>
                                <th className={classes.th} />
                                {columnGroups.map(group =>
                                    subColumns.map(sub => (
                                        <th
                                            key={`${group}-${sub}`}
                                            className={`${classes.th} ${classes.subHeader}`}
                                        >
                                            {sub}
                                        </th>
                                    ))
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map(row => {
                                const nested = getNestedData(row);
                                return (
                                    <tr key={row[rowKey]}>
                                        <td className={classes.td}>
                                            {row[rowKey]}
                                        </td>
                                        {columnGroups.map(group =>
                                            subColumns.map(sub => {
                                                const value = nested?.[group]?.[sub];
                                                return (
                                                    <td
                                                        key={`${row[rowKey]}-${group}-${sub}`}
                                                        className={classes.td}
                                                    >
                                                        {value ?? "-"}
                                                    </td>
                                                );
                                            })
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>}
        </div>
    );
};

export { GroupedMatrixTable };
export default ResponsiveWrapper(GroupedMatrixTable);